import React, { useState } from 'react';
import { Button, Card, Col, Container, Row, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull, isUndefined, startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faDownload, faLock, faPencil } from '@fortawesome/free-solid-svg-icons';
import { selectIsCustomer, selectIsFrontline, selectIsInsurer } from '@components/Auth';
import { Loading, CopyLinkButton, LoadingError, RowItem, ConfirmModal } from '@components/Base';
import { OrderStatuses, dOrderPeriod } from '@models/Order';
import { OrderBadge } from '@components/Feature/Order';
import { dUserName } from '@models/User';
import { GeneralQuoteDescriptionCardBody } from '@components/Feature/GeneralQuote';

import { useInsurancePolicyFetch } from '../hook';
import { routeInsurancePolicyBuyEditPage, routeInsurancePolicyBuyPayPage } from '../route';
import { insurancePolicyLock, InsurancePolicyLockPayload } from '../action';
import { RightCol } from '../right-col';

const ROW_ITEM_PROPS = {
  className: 'mb-2',
};

const InsurancePolicyBuyReviewPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isFrontline = useSelector(selectIsFrontline);
  const isCustomer = useSelector(selectIsCustomer);
  const isInsurer = useSelector(selectIsInsurer);
  const policy = useInsurancePolicyFetch();
  const [show, setShow] = useState(false);

  if (isUndefined(policy)) return <Loading />;
  if (isNull(policy)) return <LoadingError />;

  const {
    id,
    status,
    policyNumber,
    startedAt,
    endedAt,
    proof,
    note,
    frontlineUser,
    descriptionHtml,
    company,
  } = policy;

  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isActive = status === OrderStatuses.Active;
  const shouldEdit = isFrontline && (isPending || isLocked || isActive);
  const shouldLock = isFrontline && isPending;
  const shouldPay = isCustomer && isLocked;

  return (
    <>
      <Container className="my-3">
        <Row className="gy-5">
          <Col lg={8}>
            <Card>
              <Card.Header className="d-flex flex-row justify-content-between align-items-center">
                {t('common:summary')}
                <CopyLinkButton />
              </Card.Header>
              <Card.Body>
                <Card.Title>{t('common:policyDetails')}</Card.Title>
                <RowItem {...ROW_ITEM_PROPS} label={t('common:policyNumber')} value={policyNumber} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:theInsuredPolicyholder')} value={company?.name || ''} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:periodOfInsurance')} value={dOrderPeriod(startedAt, endedAt)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:status')} value={<OrderBadge status={status} />} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:frontlineUser')} value={dUserName(frontlineUser)} />

                {(isFrontline || isInsurer) && (
                  <>
                    <RowItem
                      {...ROW_ITEM_PROPS}
                      label={t('common:proof')}
                      value={(
                        <a href={proof} target="_blank" rel="noreferrer noopener" >
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          {t('proof')}
                        </a>
                      )}
                    />

                    <RowItem {...ROW_ITEM_PROPS} label={t('common:note')} value={note} />
                  </>
                )}
              </Card.Body>

              <GeneralQuoteDescriptionCardBody descriptionHtml={descriptionHtml} />
            </Card>
          </Col>

          <RightCol policy={policy}>
            <Card.Body>
              {/* {isFrontline && (
            <div className="text-end">
              <OrderInvoiceLink orderEncryptedId={orderEncryptedId} />
            </div>
          )} */}

              <Stack direction="vertical" gap={2}>
                {shouldEdit && (
                  <Button as={Link} to={routeInsurancePolicyBuyEditPage({ insurancePolicyId: id })} variant="outline-primary">
                    <FontAwesomeIcon icon={faPencil} className="me-2" />
                    {t('common:edit')}
                  </Button>
                )}

                {shouldLock && (
                  <Button onClick={() => setShow(true)} variant="warning">
                    <FontAwesomeIcon icon={faLock} className="me-2" />
                    {t('common:confirm')}
                  </Button>
                )}

                {shouldPay || true && (
                  <Button as={Link} to={routeInsurancePolicyBuyPayPage({ insurancePolicyId: id })}>
                    <span className="me-2">{startCase(t('common:payNow'))}</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Button>
                )}
              </Stack>
            </Card.Body>
          </RightCol>
        </Row>
      </Container>

      {shouldLock && (
        <ConfirmModal
          show={show}
          onHide={() => setShow(false)}
          onConfirm={() => {
            const payload: InsurancePolicyLockPayload = { id };
            dispatch(insurancePolicyLock(payload));
          }}
        />
      )}
    </>
  );
};

export { InsurancePolicyBuyReviewPage };
